import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, from, map, switchMap } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';

import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import { INotification, INotificationListResponse } from '../interfaces/notification.interface';

@Injectable({ providedIn: 'root' })
export class NotificationApiService extends BaseService {
	protected readonly apiUrl: string;
	private readonly handleServiceError = (error: unknown): Observable<never> => {
		if (error instanceof Error || (typeof error === 'object' && error !== null)) {
			return super.serviceError(error);
		}
		return super.serviceError({});
	};

	constructor(
		protected readonly http: HttpClient,
		private readonly auth: AuthService,
	) {
		super();
		this.apiUrl = `${environment.mainApiUrl}notification/`;
	}

	stream(): Observable<INotification> {
		return from(this.auth.getAccessTokenSilently()).pipe(
			switchMap(token => new Observable<INotification>(observer => {
				const url = `${this.apiUrl}stream?token=${encodeURIComponent(token)}`;
				const eventSource = new EventSource(url);

				eventSource.onmessage = (event: MessageEvent) => {
					observer.next(JSON.parse(event.data as string) as INotification);
				};

				eventSource.onerror = () => {
					eventSource.close();
					observer.error('SSE connection lost');
				};

				return () => eventSource.close();
			}))
		);
	}

	list(page = 1, limit = 50): Observable<INotificationListResponse> {
		const params = new HttpParams()
			.set('page', page.toString())
			.set('limit', limit.toString());
		return this.http.get<INotificationListResponse>(this.apiUrl, { params })
			.pipe(catchError(this.handleServiceError));
	}

	/**
	 * Reads only the unread total from the list endpoint (one item per page, since just the total is
	 * needed), used to keep the inbox badge in sync without loading the notification list.
	 */
	unreadCount(): Observable<number> {
		const params = new HttpParams()
			.set('unread', 'true')
			.set('page', '1')
			.set('limit', '1');
		return this.http.get<INotificationListResponse>(this.apiUrl, { params })
			.pipe(map(response => response.total), catchError(this.handleServiceError));
	}

	read(id: string): Observable<INotification> {
		return this.http.patch<INotification>(`${this.apiUrl}${id}/read`, {})
			.pipe(catchError(this.handleServiceError));
	}

	readAll(): Observable<void> {
		return this.http.patch<void>(`${this.apiUrl}read-all`, {})
			.pipe(catchError(this.handleServiceError));
	}

	archive(id: string): Observable<INotification> {
		return this.http.delete<INotification>(`${this.apiUrl}${id}`)
			.pipe(catchError(this.handleServiceError));
	}

	archiveAll(): Observable<void> {
		return this.http.delete<void>(`${this.apiUrl}archive-all`)
			.pipe(catchError(this.handleServiceError));
	}
}
